import { AxiosInstance, AxiosResponse } from "axios";
import { deleteWithAuth, getWithAuth, putWithAuth } from "../InitializeService";
import { LinkCard } from "../../types/home/links";

/**
 * MySite home / header / links-menu data from the Enterprise API's `api/mysite/*`
 * endpoints (feature 086) — the REST replacement for the MySiteGraphAPI GraphQL app.
 * Every action takes an explicit `loginName` (MySiteV2's "on behalf of" mode passes the
 * impersonated user) and needs the Azure AD bearer the shared axios instance carries.
 * Responses are plain camelCase arrays; nothing here is OData.
 */

/** One client on the home page / Service Request widget (`GET mysite/clients`). */
export interface MySiteClient {
  id: number;
  name: string;
  /** Encrypted client id used by PBS Live group lookups and the availability save; may be null. */
  encryptedId: string | null;
}

/** One badge in the header trophy menu (`GET mysite/badges`). */
export interface MySiteBadge {
  title: string;
  icon: string | null;
  description: string | null;
  startDate?: string | null;
  metadata?: string | null;
}

/** Strips a leading `DOMAIN\` so the API always receives the bare login. */
export const bareLogin = (loginName: string): string => {
  const parts = (loginName || "").split("\\");
  return parts[parts.length - 1];
};

const withLogin = (path: string, loginName: string) =>
  `mysite/${path}?loginName=${encodeURIComponent(bareLogin(loginName))}`;

export const getMySiteClients = (loginName: string, instance: AxiosInstance) =>
  getWithAuth(withLogin("clients", loginName), instance) as Promise<AxiosResponse<MySiteClient[]>>;

export const getMySiteBadges = (loginName: string, instance: AxiosInstance) =>
  getWithAuth(withLogin("badges", loginName), instance) as Promise<AxiosResponse<MySiteBadge[]>>;

/** The All Links tab — `category` is populated (the GraphQL app never selected it). */
export const getMySiteLinks = (loginName: string, instance: AxiosInstance) =>
  getWithAuth(withLogin("links", loginName), instance) as Promise<AxiosResponse<LinkCard[]>>;

/** The Recent Links tab — `[]` (not an error) when Application Insights is unavailable. */
export const getMySiteRecentLinks = (loginName: string, instance: AxiosInstance) =>
  getWithAuth(withLogin("links/recent", loginName), instance) as Promise<AxiosResponse<LinkCard[]>>;

export const getMySiteFavoriteLinks = (loginName: string, instance: AxiosInstance) =>
  getWithAuth(withLogin("links/favorites", loginName), instance) as Promise<AxiosResponse<LinkCard[]>>;

/** Idempotent on title; 204 on success, 503 when the favourites store is down. */
export const addFavoriteLink = (loginName: string, link: LinkCard, instance: AxiosInstance) =>
  putWithAuth(
    withLogin("links/favorites", loginName),
    {
      title: link.title,
      url: link.url,
      icon: link.icon,
      description: link.description,
      category: link.category ?? null,
    },
    instance,
  );

/** A no-op when the link is not a favourite. The title is the favourite's identity. */
export const removeFavoriteLink = (loginName: string, title: string, instance: AxiosInstance) =>
  deleteWithAuth(withLogin(`links/favorites/${encodeURIComponent(title)}`, loginName), instance);
